import React from 'react';
import _ from 'lodash';

import {
  useFieldArray,
  useForm
} from 'react-hook-form';
import {
  BaseTier1ReturnAuthorizationModal,
  appValidationUtil,
  selectFields,
  stepUtil
} from 'modules';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  Add,
  DeleteForever,
  RestartAlt
} from '@mui/icons-material';
import {
  errorUtil,
  fields
} from 'iron-forge-react-modules';

import * as dataService from './dataService';
import * as types from './types';



const {
  genController: genItemSelectFieldController
} = selectFields.ItemSelectField.genComponentAndControllerGenerator();
const {
  genController: genStoreSelectFieldController
} = selectFields.StoreSelectField.genComponentAndControllerGenerator({
  types: ['cleoTier1']
});
const ControlledItemSelectField = genItemSelectFieldController<types.IFormState>();
const ControlledStoreSelectField = genStoreSelectFieldController<types.IFormState>();
const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();



export type IItemReceipt = types.IItemReceipt;

export interface IProps {
  itemsToReceive?: IItemReceipt[];
  onClose: () => void;
}



const __steps = ['imei', 'items', 'partners', 'lookUpReturns', 'confirm'] as const;
export const FC: React.FC<IProps> = props => {
  const {
    step,
    goToPrev,
    goToNext
  } = stepUtil.useSteps({
    steps: __steps,
    onReachedStart: () => props.onClose()
  });

  const [
    receiveReturnAuthorization,
    receiveReturnAuthorizationTuple
  ] = dataService.useReceiveReturnAuthorization();
  const lookUpReturns = dataService.useLookUpReturnAuthorizationByDetails();
  const form = useForm<types.IFormState>({
    defaultValues: {
      lines: props.itemsToReceive ?? []
    }
  });

  const fieldArray = useFieldArray({
    control: form.control,
    name: 'lines'
  });

  const [submittedForm, setSubmittedForm] = React.useState<types.IFormState>({
    lines: []
  });



  const handleClickAdd = React.useCallback(
    () => {
      fieldArray.append({
        returnAuthorizationId: '',
        item: null,
        store: null,
        serialNumber: ''
      });
    },
    [fieldArray]
  );



  const processSubmit = React.useCallback(
    async (formState: types.IFormState) => {
      const hasUnmatchedReturns = _.some(formState.lines, line => !line.returnAuthorizationId);
      if (hasUnmatchedReturns) {
        if (step === 'lookUpReturns') {
          const lookUpResults = await lookUpReturns(formState);
          _.forEach(lookUpResults, (returnAuthorizationId, index) => {
            if (returnAuthorizationId) {
              fieldArray.update(index, {
                ...formState.lines[index],
                returnAuthorizationId
              });
              formState.lines[index].returnAuthorizationId = returnAuthorizationId;
            }
          });

          if (_.some(formState.lines, line => !line.returnAuthorizationId)) {
            window.alert("A return could not be found for one or more IMEIs.");
          } else {
            goToNext();
          }

        } else {
          goToNext();
        }

      } else {
        if (step === 'confirm') {
          await errorUtil.errorWrap(async () => {
            await receiveReturnAuthorization(formState);
            props.onClose();
          });

        } else if (step === 'partners') {
          goToNext('confirm');

        } else {
          goToNext();
        }
      }
      setSubmittedForm({
        ...formState
      });
    },
    [
      step,
      lookUpReturns,
      fieldArray,
      goToNext,
      receiveReturnAuthorization,
      props
    ]
  );



  const handleClickReset = React.useCallback(
    () => {
      goToNext('imei');
      form.reset();
    },
    [
      form,
      goToNext
    ]
  );

  const handleBack = React.useCallback(
    () => {
      if (step === 'confirm') {
        goToPrev('partners');
      } else {
        goToPrev();
      }
    },
    [
      goToPrev,
      step
    ]
  );



  return (
    <BaseTier1ReturnAuthorizationModal.FC
      title="Bulk Receive"
      primaryText={__step2PrimaryText[step]}
      secondaryText={__step2SecondaryText[step]}
      onBack={handleBack}
      onClose={props.onClose}
      onSubmit={form.handleSubmit(processSubmit)}
      disableButtons={receiveReturnAuthorizationTuple.loading}
      overrideCloseButtonArgs={{ label: step === 'imei' ? "Cancel" : "Previous" }}
      overrideSubmitButtonArgs={{ label: step === 'confirm' ? "Submit" : "Next" }}>
      {step === 'confirm' ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  IMEI
                </TableCell>

                <TableCell>
                  Item
                </TableCell>

                <TableCell>
                  Partner
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {submittedForm.lines.map(field => (
                <TableRow key={field.returnAuthorizationId}>
                  <TableCell sx={{ fontSize: 12 }}>
                    {field.serialNumber}
                  </TableCell>

                  <TableCell sx={{ fontSize: 12 }}>
                    {field.item?.displayName}
                  </TableCell>

                  <TableCell sx={{ fontSize: 12 }}>
                    {field.store?.displayName}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

      ) : (

        <Grid
          container
          spacing={2}>
          <Grid
            item
            xs={12}
            sx={{ display: 'flex' }}>
            {step === 'imei' && (
              <Button
                startIcon={<Add />}
                onClick={handleClickAdd}>
                Add
              </Button>
            )}

            <Box sx={{ flex: 1 }} />

            <Button
              disabled={receiveReturnAuthorizationTuple.loading}
              startIcon={<RestartAlt />}
              onClick={handleClickReset}>
              Reset
            </Button>
          </Grid>

          {step !== 'lookUpReturns' && fieldArray.fields.map((value, index) => (
            <React.Fragment key={value.id}>
              <Grid
                xs={10}
                item>
                {step === 'imei' && (
                  <ControlledTextField
                    isRequired
                    disableDefaultMargins
                    formControl={form.control}
                    extraValidationRules={{ pattern: appValidationUtil.reImei }}
                    fieldKey={`lines.${index}.serialNumber`} />
                )}

                {step === 'items' && (
                  <Box>
                    <ControlledItemSelectField
                      isRequired
                      disableDefaultMargins
                      formControl={form.control}
                      label="Item"
                      fieldKey={`lines.${index}.item`} />

                    <Typography variant="caption">
                      IMEI: {value.serialNumber || (submittedForm.lines[index]?.serialNumber ?? '-')}
                    </Typography>
                  </Box>
                )}

                {step === 'partners' && (
                  <Box>
                    <ControlledStoreSelectField
                      isDisabled={!!value.store}
                      isRequired
                      disableDefaultMargins
                      formControl={form.control}
                      fieldKey={`lines.${index}.store`}
                      label="Partner" />
                    <Typography variant="caption">
                      IMEI: {value.serialNumber || (submittedForm.lines[index]?.serialNumber ?? '-')}
                    </Typography>
                  </Box>
                )}
              </Grid>

              <Grid
                item
                xs={2}>
                <IconButton onClick={() => fieldArray.remove(index)}>
                  <DeleteForever />
                </IconButton>
              </Grid>
            </React.Fragment>
          ))}

          {step === 'lookUpReturns' && (
            <Grid item xs={12}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        IMEI
                      </TableCell>

                      <TableCell>
                        Item
                      </TableCell>

                      <TableCell>
                        Partner
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {submittedForm.lines.filter(field => !field.returnAuthorizationId).map((field, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ fontSize: 12 }}>
                          {field.serialNumber}
                        </TableCell>

                        <TableCell sx={{ fontSize: 12 }}>
                          {field.item?.displayName}
                        </TableCell>

                        <TableCell sx={{ fontSize: 12 }}>
                          {field.store?.displayName}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
        </Grid>
      )}
    </BaseTier1ReturnAuthorizationModal.FC>
  );
};



const __step2PrimaryText: Record<typeof __steps[number], string> = {
  imei: "Enter IMEIs",
  items: "Confirm items",
  partners: "Confirm Partners",
  lookUpReturns: "Match unassociated returns",
  confirm: "Confirm Receipts"
};

const __step2SecondaryText: Record<typeof __steps[number], string> = {
  imei: "Enter the IMEIs to receive",
  items: "Confirm the items associated with the entered IMEIs",
  partners: "Confirm partners associated with each RMA",
  lookUpReturns: "These items will be matched to a return sharing the same item without an expected serial number.",
  confirm: "Confirm receipt details"
};
